import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Package, ArrowLeft, BarChart3, MessageSquare, Box, Activity, History, Ticket, TrendingUp } from 'lucide-react';
import { API_URL, ADMIN_TOKEN_KEY, adminFetch } from './admin/shared';
import { OrdersTab } from './admin/OrdersTab';
import { ContactsTab } from './admin/ContactsTab';
import { InventoryTab } from './admin/InventoryTab';
import { StockTab } from './admin/StockTab';
import { CouponsTab } from './admin/CouponsTab';
import { StatsTab } from './admin/StatsTab';
import { ActivityTab } from './admin/ActivityTab';
import { HealthTab } from './admin/HealthTab';

const tabs = [
  { id: 'orders', label: 'Pedidos', icon: Package },
  { id: 'contacts', label: 'Mensajes', icon: MessageSquare },
  { id: 'inventory', label: 'Inventario', icon: Box },
  { id: 'stock', label: 'Stock', icon: TrendingUp },
  { id: 'coupons', label: 'Cupones', icon: Ticket },
  { id: 'stats', label: 'Estadísticas', icon: BarChart3 },
  { id: 'activity', label: 'Actividad', icon: History },
  { id: 'health', label: 'Salud', icon: Activity },
];
const AdminPanel = ({ onClose }) => {
  const [authenticated, setAuthenticated] = useState(() => !!sessionStorage.getItem(ADMIN_TOKEN_KEY));
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const [unreadCount, setUnreadCount] = useState(0);

  // React to backend-confirmed invalid token (from any admin API call) by returning to login.
  // Does NOT force a full page reload — just toggles auth state. Transient network errors won't trigger this.
  useEffect(() => {
    const handler = () => {
      setAuthenticated(false);
      setError('Tu sesión expiró. Ingresa la contraseña nuevamente.');
    };
    window.addEventListener('adminUnauthorized', handler);
    return () => window.removeEventListener('adminUnauthorized', handler);
  }, []);

  const handleHardRefresh = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
    } catch {}
    window.location.reload(true);
  };

  // Verify password against backend BEFORE persisting. Prevents kick-out loops caused by
  // mobile keyboards auto-capitalizing or appending whitespace to the password.
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    const pwd = password.trim();
    if (!pwd) {
      setError('Ingresa la contraseña');
      return;
    }
    setLoggingIn(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'X-Admin-Token': pwd },
      });
      if (res.status === 200) {
        sessionStorage.setItem(ADMIN_TOKEN_KEY, pwd);
        setAuthenticated(true);
        setPassword('');
        setError('');
      } else if (res.status === 401) {
        setError('Contraseña incorrecta');
      } else {
        setError(`Error del servidor (${res.status}). Intenta de nuevo.`);
      }
    } catch {
      setError('No se pudo conectar. Revisa tu internet e intenta de nuevo.');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthenticated(false);
    setPassword('');
    onClose();
  };

  // Fetch unread contacts count
  useEffect(() => {
    if (!authenticated) return;
    const fetchUnread = async () => {
      try {
        const res = await adminFetch(`/api/admin/stats`);
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.unread_contacts || 0);
        }
      } catch (err) {
        console.error('Failed to fetch admin stats:', err);
      }
    };
    fetchUnread();
  }, [authenticated, activeTab]);

  if (!authenticated) {
    return (
      <div className="fixed inset-0 bg-slate-900 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-sm p-8 bg-white">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Lock className="w-7 h-7 text-cyan-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Panel Admin</h2>
            <p className="text-sm text-slate-500 mt-1">Ingresa la contraseña</p>
          </div>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Contraseña"
              data-testid="admin-password"
              autoFocus
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              autoComplete="current-password"
            />
            {error && <p className="text-red-500 text-sm mb-3" data-testid="admin-login-error">{error}</p>}
            <Button type="submit" disabled={loggingIn} className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-60" data-testid="admin-login-btn">
              {loggingIn ? 'Verificando...' : 'Entrar'}
            </Button>
          </form>
          <Button variant="ghost" className="w-full mt-3 text-slate-500" onClick={onClose}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver
          </Button>
          <button
            type="button"
            onClick={handleHardRefresh}
            className="w-full mt-2 text-xs text-slate-400 hover:text-cyan-600 underline"
            data-testid="admin-hard-refresh"
          >
            ¿Problemas para entrar? Forzar actualización
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-50 z-50 overflow-y-auto" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-2.5">
          <h1 className="text-lg font-bold text-slate-800">Panel Admin</h1>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-close">
            <ArrowLeft className="w-4 h-4 mr-1" /> Salir
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-3 bg-white rounded-lg p-1 shadow-sm border" data-testid="admin-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all flex-1 justify-center relative ${
                activeTab === tab.id
                  ? 'bg-cyan-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.id === 'contacts' && unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'orders' && <OrdersTab />}
        {activeTab === 'contacts' && <ContactsTab onRead={() => setUnreadCount(c => Math.max(0, c - 1))} />}
        {activeTab === 'inventory' && <InventoryTab />}
        {activeTab === 'stock' && <StockTab />}
        {activeTab === 'coupons' && <CouponsTab />}
        {activeTab === 'stats' && <StatsTab />}
        {activeTab === 'activity' && <ActivityTab />}
        {activeTab === 'health' && <HealthTab />}
      </div>
    </div>
  );
};

export default AdminPanel;
